import React, { useState, useEffect, useRef } from 'react';
import { subscribeRoom } from '../shared/roomTx';
import { getRemainingTime, getPlayerBySlot } from '../shared/chainLogic';
import {
  FL_ROOM_STATUS,
  CHAIN_PHASE,
  FL_GAME_MODE,
} from '../shared/fiveLeague';
import './ChainTV.css';

function ChainTV({ roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showCorrect, setShowCorrect] = useState(false);
  const [showWrong, setShowWrong] = useState(false);

  const timerRef = useRef(null);
  const prevPhaseRef = useRef(null);

  // ルーム購読
  useEffect(() => {
    if (!roomId) return;

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      setRoom(data);
    });

    return () => unsub();
  }, [roomId]);

  // タイマー更新
  useEffect(() => {
    if (!room?.game?.endsAt) return;

    const updateTimer = () => {
      const remaining = getRemainingTime(room.game.endsAt);
      setRemainingTime(remaining);
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 100);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [room?.game?.endsAt]);

  // フェーズ変更によるアニメーション
  useEffect(() => {
    if (!room?.game) return;
    const { phase } = room.game;
    const prevPhase = prevPhaseRef.current;

    if (phase === CHAIN_PHASE.CORRECT && prevPhase !== CHAIN_PHASE.CORRECT) {
      setShowCorrect(true);
      setTimeout(() => setShowCorrect(false), 1500);
    }

    if (phase === CHAIN_PHASE.WRONG && prevPhase !== CHAIN_PHASE.WRONG) {
      setShowWrong(true);
      setTimeout(() => setShowWrong(false), 1500);
    }

    prevPhaseRef.current = phase;
  }, [room?.game?.phase]);

  if (loading) {
    return (
      <div className="chain-tv-container">
        <div className="chain-tv-loading">読み込み中...</div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="chain-tv-container">
        <div className="chain-tv-error">
          <p>ルームが見つかりません</p>
          <p className="chain-tv-hint">ルームコードを確認してください</p>
        </div>
      </div>
    );
  }

  // ワンワードモードの場合はTVDisplayへリダイレクト案内
  if (room.gameMode !== FL_GAME_MODE.CHAIN) {
    return (
      <div className="chain-tv-container">
        <div className="chain-tv-error">
          <p>このルームはワンワードモードです</p>
          <p className="chain-tv-hint">TV表示を使用してください</p>
        </div>
      </div>
    );
  }

  const game = room.game;
  if (!game) {
    return (
      <div className="chain-tv-container">
        <div className="chain-tv-waiting">
          <h1 className="chain-tv-title">⚡ ANSWER CHAIN</h1>
          <p className="chain-tv-subtitle">ゲーム開始を待っています...</p>
          <p className="chain-tv-room-code">{room.roomCode}</p>
        </div>
      </div>
    );
  }

  const controllers = room.players?.filter(p => p.role !== 'display') || [];
  const currentPlayer = game.currentPlayerSlot !== null
    ? getPlayerBySlot(game.currentPlayerSlot, room.playerOrder, room.players)
    : null;

  // 結果画面
  if (game.phase === CHAIN_PHASE.RESULT || room.status === FL_ROOM_STATUS.FINISHED) {
    const isPerfect = game.correctCount === game.answerCount;
    return (
      <div className="chain-tv-container result-phase">
        <div className="chain-tv-result">
          <div className="result-header">
            <span className="result-icon">{isPerfect ? '🎉' : '💥'}</span>
            <h1 className="result-title">{isPerfect ? 'PERFECT!' : 'GAME OVER'}</h1>
          </div>
          <div className="result-score">
            <span className="score-value">{game.correctCount}</span>
            <span className="score-separator">/</span>
            <span className="score-total">{game.answerCount}</span>
          </div>
          <div className="result-answers">
            {game.submittedAnswers?.map((answer, i) => (
              <div key={i} className="result-answer">
                <span className="result-answer-num">{i + 1}</span>
                <span className="result-answer-text">{answer.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // タイムアウト
  if (game.phase === CHAIN_PHASE.TIMEOUT) {
    return (
      <div className="chain-tv-container timeout-phase">
        <div className="chain-tv-timeout">
          <span className="timeout-icon">💥</span>
          <h1 className="timeout-title">TIME UP!</h1>
          <p className="timeout-score">{game.correctCount} / {game.answerCount}</p>
        </div>
      </div>
    );
  }

  // 全問正解
  if (game.phase === CHAIN_PHASE.COMPLETE) {
    return (
      <div className="chain-tv-container complete-phase">
        <div className="chain-tv-complete">
          <span className="complete-icon">🎉</span>
          <h1 className="complete-title">PERFECT!</h1>
          <p className="complete-score">{game.answerCount}問 全問正解！</p>
        </div>
      </div>
    );
  }

  // 問題読み上げフェーズ
  if (game.phase === CHAIN_PHASE.READING) {
    return (
      <div className="chain-tv-container reading-phase">
        <div className="chain-tv-reading">
          <h1 className="reading-title">⚡ ANSWER CHAIN</h1>
          <div className="question-display reading">
            <p className="question-text">{game.prompt}</p>
          </div>
          <p className="reading-hint">問題を確認中...</p>
        </div>
      </div>
    );
  }

  // メインゲーム画面
  return (
    <div className="chain-tv-container game-phase">
      {/* 正解オーバーレイ */}
      {showCorrect && (
        <div className="tv-overlay correct-overlay">
          <span className="overlay-icon">⭕</span>
          <span className="overlay-text">正解！</span>
        </div>
      )}

      {/* 不正解オーバーレイ */}
      {showWrong && (
        <div className="tv-overlay wrong-overlay">
          <span className="overlay-icon">❌</span>
          <span className="overlay-text">
            {game.lastWrongReason === 'duplicate' ? 'かぶり！' : 'ブブー'}
          </span>
        </div>
      )}

      {/* ヘッダー */}
      <div className="chain-tv-header">
        <div className="tv-timer">
          <span className={`timer-num ${remainingTime <= 10 ? 'warning' : ''} ${remainingTime <= 5 ? 'critical' : ''}`}>
            {remainingTime}
          </span>
        </div>
        <div className="tv-title">⚡ ANSWER CHAIN</div>
        <div className="tv-progress">
          <span className="progress-num">{game.correctCount}</span>
          <span className="progress-sep">/</span>
          <span className="progress-total">{game.answerCount}</span>
        </div>
      </div>

      {/* 問題表示 */}
      <div className="question-display">
        <p className="question-text">{game.prompt}</p>
      </div>

      {/* 回答済みリスト */}
      <div className="tv-answers">
        {game.submittedAnswers?.map((answer, i) => (
          <div key={i} className="tv-answer">
            <span className="tv-answer-num">{i + 1}</span>
            <span className="tv-answer-text">{answer.text}</span>
          </div>
        ))}
      </div>

      {/* 現在のプレイヤー */}
      <div className="tv-current-player">
        <span className="current-label">回答者</span>
        <span className="current-name">{currentPlayer?.nickname || '???'}</span>
      </div>

      {/* プレイヤーリスト */}
      <div className="tv-player-list">
        {controllers.map((player, i) => {
          const slot = room.playerOrder.indexOf(player.playerId);
          const isCurrent = slot === game.currentPlayerSlot;
          return (
            <div key={player.playerId} className={`tv-player ${isCurrent ? 'current' : ''}`}>
              {player.nickname}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ChainTV;
